import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../../../components/page-context/ContextProvider";

// TODO: BACKEND - Import when ready
// import { fetchWithToken } from "../../../authentication/AuthProvider";
// import config from "../../../config";

const useNetZeroData = (buildingId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!buildingId) {
      setLoading(false);
      return;
    }

    // TODO: BACKEND - Replace with API call when ready
    setTimeout(() => {
      setData({
        currentEmissions: 4136,
        target2030: 1650,
        target2050: 0,
        trajectory: [
          { year: 2024, baseline: 4136, current: 4136, target: 4136, sbti: 4136 },
          { year: 2025, baseline: 4136, current: 3950, target: 3930, sbti: 3930 },
          { year: 2026, baseline: 4136, current: 3680, target: 3640, sbti: 3640 },
          { year: 2028, baseline: 4136, current: 2890, target: 2800, sbti: 2800 },
          { year: 2030, baseline: 4136, current: 1850, target: 1650, sbti: 1650 },
          { year: 2040, baseline: 4136, current: 850, target: 0, sbti: 0 },
          { year: 2050, baseline: 4136, current: 0, target: 0, sbti: 0 },
        ],
        strategies: [
          {
            strategy: "Energy Efficiency Upgrades",
            category: "Operational",
            reduction: 890,
            percentage: 21.5,
            cost: "$125,000",
            timeline: "2024-2026",
            status: "planned",
            icon: "⚡",
          },
          {
            strategy: "Solar PV Installation",
            category: "Renewable Energy",
            reduction: 1250,
            percentage: 30.2,
            cost: "$850,000",
            timeline: "2025-2027",
            status: "planned",
            icon: "🌱",
          },
          {
            strategy: "Heat Pump Conversion",
            category: "Electrification",
            reduction: 650,
            percentage: 15.7,
            cost: "$95,000",
            timeline: "2026-2028",
            status: "planning",
            icon: "❄️",
          },
        ],
        sbtiTargets: {
          scope1: {
            name: "Scope 1",
            desc: "Direct emissions from operations",
            current: 245,
            target2030: 122,
            reduction: 50,
          },
          scope2: {
            name: "Scope 2",
            desc: "Indirect emissions from purchased energy",
            current: 1890,
            target2030: 189,
            reduction: 90,
          },
          scope3: {
            name: "Scope 3",
            desc: "Value chain emissions",
            current: 2001,
            target2030: 1340,
            reduction: 33,
          },
        },
        sbtiValidation: [
          { item: "Target Ambition Level", status: "Validated" },
          { item: "Scope Coverage", status: "Complete" },
          { item: "Net Zero Commitment", status: "Committed" },
        ],
      });
      setLoading(false);
    }, 500);
  }, [buildingId]);

  return { data, loading };
};

const NetZeroTab = () => {
  const { t } = useTranslation();
  const { selectedBuildings } = useContextProvider();
  const [activeTab, setActiveTab] = useState("trajectory");
  
  const buildingId = selectedBuildings && selectedBuildings.length === 1 ? selectedBuildings[0].id : null;
  const selectedBuilding = selectedBuildings && selectedBuildings.length === 1 ? selectedBuildings[0] : null;
  
  const { data, loading } = useNetZeroData(buildingId);

  if (!selectedBuilding) {
    return (
      <div style={{ padding: "20px", color: "#666" }}>
        {t("Please select a building from the dashboard to view Net Zero analysis.")}
      </div>
    );
  }

  if (loading) return <div style={{ padding: "20px", color: "#666" }}>{t("Loading Net Zero data...")}</div>;
  if (!data) return <div style={{ padding: "20px", color: "#666" }}>{t("No Net Zero data available.")}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* HEADER */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#1a1a1a" }}>
            {t("Net Zero Analysis")} - {selectedBuilding.name}
          </h2>
          <div style={{ display: "flex", gap: "8px" }}>
            <div style={{ background: "#ecfdf5", color: "#10b981", padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 }}>
              ✓ {t("SBTi Aligned")}
            </div>
            <div style={{ background: "#fafbfc", color: "#666", border: "1px solid #e0e0e0", padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 }}>
              {t("Net Zero 2050")}
            </div>
          </div>
        </div>
        <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
          {t("Science Based Targets initiative (SBTi) compliance and net zero pathway")}
        </p>
      </div>

      {/* TABS */}
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e0e0e0", background: "#fafbfc" }}>
          {[
            { id: "trajectory", label: "Carbon Trajectory" },
            { id: "strategies", label: "Strategies" },
            { id: "sbti", label: "SBTi Compliance" },
          ].map((tabItem) => (
            <button
              key={tabItem.id}
              onClick={() => setActiveTab(tabItem.id)}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === tabItem.id ? 600 : 500,
                color: activeTab === tabItem.id ? "#1a1a1a" : "#999",
                borderBottom: activeTab === tabItem.id ? "2px solid #1a1a1a" : "none",
                transition: "all 0.2s ease",
              }}
            >
              {t(tabItem.label)}
            </button>
          ))}
        </div>

        <div style={{ padding: "24px" }}>
          {activeTab === "trajectory" && (
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 8px 0", color: "#1a1a1a" }}>
                {t("Net Zero Pathway")}
              </h3>
              <p style={{ fontSize: "13px", color: "#666", margin: "0 0 20px 0" }}>
                {t("Carbon emissions trajectory toward net zero by 2050")}
              </p>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={data.trajectory} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={true} />
                  <XAxis dataKey="year" stroke="#999" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#999" style={{ fontSize: "12px" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "6px" }} />
                  <Line type="monotone" dataKey="baseline" stroke="#999" strokeDasharray="5 5" strokeWidth={2} name="Business as Usual" dot={false} />
                  <Line type="monotone" dataKey="target" stroke="#10b981" strokeWidth={2} name="SBTi Target" dot={{ fill: "#10b981", r: 4 }} />
                  <Line type="monotone" dataKey="current" stroke="#10b981" strokeWidth={3} name="Current Trajectory" dot={{ fill: "#10b981", r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === "strategies" && (
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 8px 0", color: "#1a1a1a" }}>
                {t("Decarbonization Strategies")}
              </h3>
              <p style={{ fontSize: "13px", color: "#666", margin: "0 0 20px 0" }}>
                {t("Portfolio of initiatives to achieve net zero emissions")}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {data.strategies.map((strat, idx) => (
                  <div key={idx} style={{ background: "#fafbfc", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                        <div style={{ fontSize: "20px" }}>{strat.icon}</div>
                        <div>
                          <h4 style={{ fontSize: "14px", fontWeight: 600, margin: "0", color: "#1a1a1a" }}>
                            {t(strat.strategy)}
                          </h4>
                          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
                            {t(strat.category)}
                          </p>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "16px", fontWeight: 600, color: "#10b981" }}>
                          -{strat.reduction} tCO2e
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {strat.percentage}%
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "12px", fontSize: "12px" }}>
                      <div>
                        <span style={{ color: "#999" }}>{t("Cost:")}</span>
                        <div style={{ fontWeight: 600, color: "#1a1a1a", marginTop: "4px" }}>
                          {strat.cost}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: "#999" }}>{t("Timeline:")}</span>
                        <div style={{ fontWeight: 600, color: "#1a1a1a", marginTop: "4px" }}>
                          {strat.timeline}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            background: strat.status === "planned" ? "#f3f4f6" : "#fef3c7",
                            color: strat.status === "planned" ? "#666" : "#d97706",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "capitalize",
                            display: "inline-block",
                          }}
                        >
                          {t(strat.status)}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      width: "100%",
                      height: "6px",
                      background: "#e0e0e0",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        width: `${strat.percentage}%`,
                        height: "100%",
                        background: "#10b981",
                        transition: "width 0.3s ease",
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "sbti" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                  {Object.entries(data.sbtiTargets).map(([key, scope]) => (
                    <div key={key} style={{ background: "#fafbfc", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                        <div>
                          <h4 style={{ fontSize: "16px", fontWeight: 600, margin: 0, color: "#1a1a1a" }}>
                            {t(scope.name)}
                          </h4>
                          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
                            {t(scope.desc)}
                          </p>
                        </div>
                        <div style={{ color: "#10b981", fontSize: "20px" }}>✓</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", marginBottom: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#666" }}>{t("Current (2024):")}</span>
                          <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{scope.current} tCO2e</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#666" }}>{t("2030 Target:")}</span>
                          <span style={{ fontWeight: 600, color: "#10b981" }}>{scope.target2030} tCO2e</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#666", fontWeight: 600 }}>{t("Reduction Required:")}</span>
                          <span style={{ fontWeight: 600, color: "#10b981" }}>{scope.reduction}%</span>
                        </div>
                      </div>

                      <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px", display: "flex", justifyContent: "space-between" }}>
                        <span>{t("Progress")}</span>
                        <span>{((scope.current - scope.target2030) / scope.current * 100 * 0.4).toFixed(0)}%</span>
                      </div>
                      <div style={{
                        width: "100%",
                        height: "6px",
                        background: "#e0e0e0",
                        borderRadius: "3px",
                        overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${((scope.current - scope.target2030) / scope.current * 100 * 0.4)}%`,
                          height: "100%",
                          background: "#10b981",
                          transition: "width 0.3s ease",
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px" }}>
                <h4 style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 8px 0", color: "#1a1a1a" }}>
                  {t("SBTi Validation Status")}
                </h4>
                <p style={{ fontSize: "12px", color: "#666", margin: "0 0 16px 0" }}>
                  {t("Compliance with Science Based Targets initiative requirements")}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {data.sbtiValidation.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        background: "#ecfdf5",
                        border: "1px solid #d1fae5",
                        borderRadius: "6px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ color: "#10b981", fontSize: "18px" }}>✓</div>
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1a1a1a" }}>
                          {t(item.item)}
                        </span>
                      </div>
                      <div
                        style={{
                          background: "#10b981",
                          color: "#fff",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {t(item.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetZeroTab;
